import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "../landing.css";
import Hero from "../components/landing/Hero";
import OlliSection from "../components/landing/OlliSection";
import Showcase from "../components/landing/Showcase";
import FeatureGrid from "../components/landing/FeatureGrid";
import StatsBand from "../components/landing/StatsBand";
import Faq from "../components/landing/Faq";
// import Pricing from "../components/landing/Pricing"; // Sezione prezzi temporaneamente nascosta (ripristinare quando serve)
import FinalCta from "../components/landing/FinalCta";

// Link di navigazione alle sezioni della landing (smooth scroll)
const NAV_LINKS = [
  { id: "funzionalita", label: "Funzionalità" },
  { id: "olli", label: "Come funziona" },
  { id: "faq", label: "FAQ" },
  // { id: "prezzi", label: "Prezzi" }, // Link prezzi temporaneamente nascosto
];

export default function LandingPage() {
  const navbarRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  // Navbar scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (navbarRef.current) {
        navbarRef.current.classList.toggle("scrolled", window.scrollY > 40);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Evidenzia la sezione attiva durante lo scroll
  useEffect(() => {
    const sections = NAV_LINKS
      .map((l) => document.getElementById(l.id))
      .filter(Boolean);
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Smooth scroll alla sezione + chiusura menu mobile
  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div className="landing-page">
      {/* NAVBAR */}
      <nav className="navbar" ref={navbarRef}>
        <div className="container">
          <Link to="/" className="logo">
            <img src="/forfy-logo-horizontal-light.svg" alt="Forfy" style={{ height: '36px', width: 'auto' }} />
          </Link>

          {/* Menu sezioni: inline su desktop, dropdown su mobile */}
          <div id="nav-menu" className={`nav-menu ${menuOpen ? "open" : ""}`}>
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                className={`nav-link ${activeSection === l.id ? "active" : ""}`}
                onClick={(e) => handleNavClick(e, l.id)}
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="nav-actions">
            <Link to="/accedi" className="nav-login">Accedi</Link>
            <button
              type="button"
              className="nav-toggle"
              aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={menuOpen}
              aria-controls="nav-menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* SEZIONE 1 — HERO */}
      <Hero />

      {/* SEZIONE 2 — OLLI */}
      <OlliSection />

      {/* SEZIONE 3.1 — SHOWCASE: Tasse e soglie */}
      <Showcase
        id="showcase-tasse"
        eyebrow="Tasse e soglie"
        title="Il netto che ti resta. Sempre aggiornato, mai a sorpresa."
        body="Forfy calcola imposta sostitutiva e INPS su ogni incasso e ti mostra quanto puoi davvero spendere. E ti avvisa prima di avvicinarti agli 85.000 €, così decidi tu come muoverti."
        imageSrc="/landing-assets/screenshots/dashboard.png"
        imageAlt="Dashboard Forfy con KPI fiscali e barra della soglia forfettario a €85.000"
      />

      {/* SEZIONE 3.2 — SHOWCASE: Fatturazione (reverse + soft) */}
      <Showcase
        id="showcase-fatture"
        eyebrow="Fatturazione"
        title="Crea, esporta, incassa. Al resto pensa Forfy."
        body="Fatture pronte in un minuto, con bollo calcolato in automatico. Esporta in PDF, traccia gli incassi e lascia che Forfy mandi i solleciti per te. L'integrazione con lo SDI? Ci stiamo lavorando."
        imageSrc="/landing-assets/screenshots/fatture.png"
        imageAlt="Tabella fatture Forfy con netto inline e fatture scadute evidenziate"
        reverse
        soft
      />

      {/* SEZIONE 3.3 — SHOWCASE: F24 */}
      <Showcase
        id="showcase-f24"
        eyebrow="F24"
        title="Anche quando dimentichi una scadenza, Forfy ci pensa."
        body="Tieni sotto controllo ogni F24 e, se salti una scadenza, Forfy calcola da solo il ravvedimento operoso — interessi e sanzioni inclusi. Sai esattamente quanto versare."
        imageSrc="/landing-assets/screenshots/f24.png"
        imageAlt="Sezione F24 di Forfy con calcolo automatico del ravvedimento operoso"
      />

      {/* SEZIONE 4 — FEATURE GRID */}
      <FeatureGrid />

      {/* SEZIONE 5 — NUMERI */}
      <StatsBand />

      {/* SEZIONE 6 — FAQ */}
      <Faq />

      {/* SEZIONE 6.5 — PREZZI (temporaneamente nascosta — per ripristinare: togliere i commenti qui, in NAV_LINKS e nell'import di Pricing) */}
      {/* <Pricing /> */}

      {/* SEZIONE 7 — CTA FINALE */}
      <FinalCta />

      {/* FOOTER */}
      <footer className="footer">
        <div className="container">
          <div className="footer-left">
            <span className="footer-wordmark">forfy</span>
            <span className="footer-copy">
              © {new Date().getFullYear()} Forfy. Tutti i diritti riservati.
            </span>
          </div>
          <div className="footer-links">
            <a href="https://www.iubenda.com/privacy-policy/43603356" className="iubenda-white iubenda-noiframe iubenda-embed" title="Privacy Policy">Privacy Policy</a>
            <a href="https://www.iubenda.com/privacy-policy/43603356/cookie-policy" className="iubenda-white iubenda-noiframe iubenda-embed" title="Cookie Policy">Cookie Policy</a>
            <a href="https://www.iubenda.com/termini-e-condizioni/43603356" className="iubenda-white iubenda-noiframe iubenda-embed" title="Termini e Condizioni">Termini e Condizioni</a>
            <a href="mailto:ciao@forfy.app">Contatti</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
